import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/client.js';
import { useAuth } from '../auth/AuthContext.jsx';
import VoiceInputButton from './VoiceInputButton.jsx';
import SearchableSelect from './SearchableSelect.jsx';

const appendText = (cur, add) => (cur && cur.trim() ? `${cur.trim()} ${add}` : add);
const ADMIN_ROLES = ['superadmin', 'dealer_admin', 'branch_admin'];
// Suggested lead sources (free text — reps can type their own).
const LEAD_SOURCE_SUGGESTIONS = ['Service referral', 'Inbound', 'Outbound', 'Referral', 'Trade show', 'Existing customer'];
const parseTags = (s) => Array.from(new Set(
  String(s || '').split(',').map(t => t.trim()).filter(Boolean)
)).slice(0, 20);

/**
 * Modal for creating or editing an opportunity.
 *
 * Props:
 *   opp     — existing opportunity row (null/undefined for new)
 *   initial — when creating, prefill with { name, customer_id, source_note_id, ... }
 *   onClose — close the modal
 *   onSaved — called on successful save
 */
export default function OpportunityEditor({ opp, initial = {}, onClose, onSaved }) {
  const qc = useQueryClient();
  const { user, hasPermission } = useAuth();
  const isAdmin = ADMIN_ROLES.includes(user?.role);

  const customers = useQuery({
    queryKey: ['customers', 'lookup'],
    queryFn: () => api.get('/customers').then(r => r.data.data),
  });

  const salespersons = useQuery({
    queryKey: ['opportunities', 'salespersons'],
    queryFn: () => api.get('/opportunities/salespersons').then(r => r.data.data),
    enabled: isAdmin,
  });

  const [form, setForm] = useState(opp ? {
    name: opp.name || '',
    description: opp.description || '',
    customer_id: opp.customer_id || '',
    customer_name_hint: opp.customer_id ? '' : (opp.customer_name_hint || ''),
    user_id: opp.user_id || '',
    stage: opp.stage || 'prospect',
    domain: opp.domain || 'other',
    value_usd: opp.value_usd || '',
    cost_usd: opp.cost_usd || '',
    probability: opp.probability ?? '',
    expected_close_date: opp.expected_close_date ? String(opp.expected_close_date).slice(0, 10) : '',
    lost_reason: opp.lost_reason || '',
    source_note_id: opp.source_note_id || null,
    lead_source: opp.lead_source || '',
    tags: Array.isArray(opp.tags) ? opp.tags.join(', ') : '',
    excede_unit_id: opp.excede_unit_id || '',
  } : {
    name: initial.name || '',
    description: initial.description || '',
    customer_id: initial.customer_id || '',
    customer_name_hint: initial.customer_id ? '' : (initial.customer_name_hint || ''),
    user_id: user?.id || '',
    stage: initial.stage || 'prospect',
    domain: initial.domain || 'other',
    value_usd: initial.value_usd || '',
    cost_usd: initial.cost_usd || '',
    probability: initial.probability ?? '',
    expected_close_date: initial.expected_close_date || '',
    lost_reason: '',
    source_note_id: initial.source_note_id || null,
    lead_source: initial.lead_source || '',
    tags: '',
    excede_unit_id: initial.excede_unit_id || '',
  });

  // The selected customer's fleet, for linking the opportunity to a unit (#65).
  const units = useQuery({
    queryKey: ['customer-units', form.customer_id],
    queryFn: () => api.get(`/customers/${form.customer_id}/units`).then(r => r.data.data),
    enabled: !!form.customer_id,
  });

  const save = useMutation({
    mutationFn: () => {
      const custId = form.customer_id ? parseInt(form.customer_id, 10) : null;
      const payload = {
        name: form.name,
        description: form.description || null,
        customer_id: custId,
        ...(!custId && form.customer_name_hint ? { customer_name_hint: form.customer_name_hint } : {}),
        user_id: isAdmin && form.user_id ? parseInt(form.user_id, 10) : undefined,
        stage: form.stage,
        domain: form.domain,
        value_usd: form.value_usd === '' ? null : Number(form.value_usd),
        cost_usd: form.cost_usd === '' ? null : Number(form.cost_usd),
        probability: form.probability === '' ? null : parseInt(form.probability, 10),
        expected_close_date: form.expected_close_date || null,
        lost_reason: form.lost_reason || null,
        source: opp ? undefined : (form.source_note_id ? 'note' : 'manual'),
        source_note_id: form.source_note_id || null,
        lead_source: form.lead_source.trim(),
        tags: parseTags(form.tags),
        excede_unit_id: form.excede_unit_id === '' ? '' : parseInt(form.excede_unit_id, 10),
      };
      if (opp) return api.put(`/opportunities/${opp.id}`, payload).then(r => r.data.data);
      return api.post('/opportunities', payload).then(r => r.data.data);
    },
    onSuccess: (row) => {
      qc.invalidateQueries({ queryKey: ['opportunities'] });
      onSaved?.(row);
    },
  });

  const remove = useMutation({
    mutationFn: () => api.delete(`/opportunities/${opp.id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['opportunities'] });
      onSaved?.();
    },
  });

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 flex items-end md:items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-md p-5 space-y-3 shadow-panel max-h-[90vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <h3 className="text-lg font-semibold">{opp ? 'Edit opportunity' : 'New opportunity'}</h3>

        <div>
          <label className="label">Name *</label>
          <div className="flex gap-2 items-center">
            <input className="input flex-1" value={form.name}
                   onChange={e => setForm({ ...form, name: e.target.value })} autoFocus />
            <VoiceInputButton title="Dictate name"
              onTranscript={t => setForm(f => ({ ...f, name: appendText(f.name, t) }))} />
          </div>
        </div>
        <div>
          <label className="label">Customer</label>
          {form.customer_name_hint && !form.customer_id && (
            <div className="text-xs text-amber-800 mb-1">
              📝 {form.customer_name_hint}{' '}
              <span className="text-[10px] px-1 py-0.5 rounded bg-amber-100 text-amber-800">not linked</span>
              {' — '}
              <button type="button" className="underline" onClick={() => setForm(f => ({ ...f, customer_name_hint: '' }))}>
                clear
              </button>
            </div>
          )}
          <SearchableSelect
            options={(customers.data ?? []).map(c => ({
              value: c.id,
              label: `${c.name}${c.city ? ` — ${[c.city, c.state].filter(Boolean).join(', ')}` : ''} (${c.account_number ?? `#${c.id}`})`,
            }))}
            value={form.customer_id}
            onChange={v => setForm({ ...form, customer_id: v, customer_name_hint: v ? '' : form.customer_name_hint, excede_unit_id: '' })}
            placeholder="Search customers…"
            emptyLabel="— None —"
          />
        </div>
        {form.customer_id && (
          <div>
            <label className="label">Linked unit (optional)</label>
            <select className="input" value={form.excede_unit_id}
                    onChange={e => setForm({ ...form, excede_unit_id: e.target.value })}>
              <option value="">— No specific unit —</option>
              {(units.data ?? []).map(u => (
                <option key={u.id} value={u.id}>
                  {[u.model_year, u.make, u.model].filter(Boolean).join(' ')}
                  {u.vin ? ` · ${u.vin}` : ''}{u.status ? ` (${u.status})` : ''}
                </option>
              ))}
            </select>
            {units.isLoading && <div className="text-xs text-muted mt-1">Loading units…</div>}
            {!units.isLoading && (units.data?.length ?? 0) === 0 && (
              <div className="text-xs text-muted mt-1">No units on file for this customer.</div>
            )}
          </div>
        )}
        {isAdmin && (
          <div>
            <label className="label">Salesperson</label>
            <SearchableSelect
              options={(salespersons.data ?? []).map(s => ({ value: s.id, label: s.full_name }))}
              value={form.user_id}
              onChange={v => setForm({ ...form, user_id: v })}
              placeholder="Search salespersons…"
              emptyLabel="— Unassigned —"
            />
          </div>
        )}
        <div className="grid grid-cols-3 gap-2">
          <div>
            <label className="label">Domain</label>
            <select className="input" value={form.domain}
                    onChange={e => setForm({ ...form, domain: e.target.value })}>
              <option value="parts">Parts</option>
              <option value="service">Service</option>
              <option value="trailer_sales">Trailer sales</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <label className="label">Value (USD)</label>
            <input className="input" type="number" step="100" value={form.value_usd}
                   onChange={e => setForm({ ...form, value_usd: e.target.value })} />
          </div>
          <div>
            <label className="label">Cost (USD)</label>
            <input className="input" type="number" step="100" value={form.cost_usd}
                   onChange={e => setForm({ ...form, cost_usd: e.target.value })} />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">Stage</label>
            <select className="input" value={form.stage}
                    onChange={e => setForm({ ...form, stage: e.target.value, probability: '' })}>
              <option value="lead">Lead</option>
              <option value="prospect">Prospect</option>
              <option value="negotiating">Negotiating</option>
              <option value="qualified">Qualified</option>
              <option value="quoted">Quoted</option>
              <option value="won">Won</option>
              <option value="lost">Lost</option>
            </select>
          </div>
          <div>
            <label className="label">Lead source</label>
            <input className="input" list="lead-source-options" placeholder="e.g. Service referral"
                   value={form.lead_source}
                   onChange={e => setForm({ ...form, lead_source: e.target.value })} />
            <datalist id="lead-source-options">
              {LEAD_SOURCE_SUGGESTIONS.map(s => <option key={s} value={s} />)}
            </datalist>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="label">Probability % (override)</label>
            <input className="input" type="number" min="0" max="100" placeholder="auto"
                   value={form.probability}
                   onChange={e => setForm({ ...form, probability: e.target.value })} />
          </div>
          <div>
            <label className="label">Expected close</label>
            <input className="input" type="date" value={form.expected_close_date}
                   onChange={e => setForm({ ...form, expected_close_date: e.target.value })} />
          </div>
        </div>
        <div>
          <label className="label">Tags</label>
          <input className="input" placeholder="comma-separated, e.g. fleet, refrigerated"
                 value={form.tags}
                 onChange={e => setForm({ ...form, tags: e.target.value })} />
        </div>
        <div>
          <label className="label">Notes</label>
          <div className="flex gap-2 items-start">
            <textarea className="input min-h-[60px] flex-1" value={form.description}
                      onChange={e => setForm({ ...form, description: e.target.value })}
                      spellCheck={true} autoCorrect="on" autoCapitalize="sentences" />
            <VoiceInputButton title="Dictate notes"
              onTranscript={t => setForm(f => ({ ...f, description: appendText(f.description, t) }))} />
          </div>
        </div>
        {form.stage === 'lost' && (
          <div>
            <label className="label">Lost reason</label>
            <input className="input" value={form.lost_reason}
                   onChange={e => setForm({ ...form, lost_reason: e.target.value })} />
          </div>
        )}

        {save.isError && (
          <div className="text-crimson text-sm">{save.error?.response?.data?.error || 'Save failed'}</div>
        )}

        <div className="flex justify-between gap-2 pt-2">
          {opp && hasPermission('can_delete_opportunities') && (
            <button className="text-crimson text-sm"
                    onClick={() => { if (window.confirm('Delete this opportunity?')) remove.mutate(); }}>
              Delete
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button className="btn-ghost" onClick={onClose}>Cancel</button>
            <button className="btn-primary" disabled={!form.name || save.isPending}
                    onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
